import type {
  StepAuthoringCommand,
  StepAuthoringFormField,
  StepCommand,
} from './workflow-step-composition';
import { isWorkflowFormLayoutSupported, resolveWorkflowFormLayout } from '@app/shared/workflow-commands/workflow-form-layout';
import { isWorkflowFormLookupBinding } from '@app/shared/workflow-commands/workflow-form-lookup-contract';
import { sameCapabilityReference } from './workflow-capability-authoring';

function exactLookupRuntime(command: StepCommand, installed: StepAuthoringCommand, code: string, contract: string): boolean {
  const saved = command?.capability, reference = installed?.capability, form = installed?.form;
  return !!saved?.key && !!reference?.key && reference.key.provider === 'workflow' && reference.key.kind === 'command'
    && reference.key.code === code && reference.key.version === 2
    && typeof saved.inputSchemaHash === 'string' && saved.inputSchemaHash.length > 0
    && sameCapabilityReference(saved, reference)
    && form?.contract === contract && form.version === 1
    && form.inputSchemaHash === saved.inputSchemaHash;
}

/** Exact Record2 runtime identity; field permissions and live ownership are separate authoring checks. */
export function hasInstalledProvidedLookupRuntime(command: StepCommand, installed: StepAuthoringCommand): boolean {
  return exactLookupRuntime(command, installed, 'workflow.review.record', 'WorkflowReviewRecordRequest');
}

/** Exact Preparation2 runtime identity; this alone grants no authoring permission. */
export function hasInstalledPreparationLookupRuntime(command: StepCommand, installed: StepAuthoringCommand): boolean {
  return exactLookupRuntime(command, installed, 'workflow.preparation.submit', 'WorkflowPreparationSubmitRequest');
}

/** Exact direct Request2 runtime identity; installation grants no authoring permission. */
export function hasInstalledReviewRequestLookupRuntime(command: StepCommand, installed: StepAuthoringCommand): boolean {
  return exactLookupRuntime(command, installed, 'workflow.review.request', 'WorkflowReviewLookupRequest');
}

const fieldKeys = new Set(['name', 'label', 'required', 'maxLength', 'control', 'optionSource', 'options', 'layout', 'lookup']);
const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
function supportedV3Field(value: unknown): boolean {
  if (!record(value) || Object.keys(value).some(key => !fieldKeys.has(key)) || typeof value['name'] !== 'string'
    || value['label'] != null && typeof value['label'] !== 'string'
    || value['required'] != null && typeof value['required'] !== 'boolean'
    || value['maxLength'] != null && (typeof value['maxLength'] !== 'number' || !Number.isFinite(value['maxLength']))
    || value['control'] != null && (typeof value['control'] !== 'string' || !['text', 'textarea', 'select'].includes(value['control']))
    || value['optionSource'] != null && (typeof value['optionSource'] !== 'string' || !['contract-options', 'step-outcomes', 'custom-options', 'lookup'].includes(value['optionSource']))) return false;
  return value['options'] == null || Array.isArray(value['options']) && value['options'].every(option => record(option)
    && !Object.keys(option).some(key => key !== 'value' && key !== 'label') && typeof option['value'] === 'string' && typeof option['label'] === 'string');
}

export function isUnsupportedFormConfiguration(command: StepCommand): boolean {
  const configuration = command.formConfiguration;
  // Legacy common edits retain unrelated metadata; the runtime reader stays strict.
  return (
    configuration != null &&
    (typeof configuration !== 'object' || Array.isArray(configuration) ||
      ![1, 2, 3].includes(configuration.version) ||
      configuration.version === 3 && Object.keys(configuration).some(key => key !== 'version' && key !== 'fields') ||
      !Array.isArray(configuration.fields) ||
      configuration.version === 3 && configuration.fields.length > 50 ||
      configuration.fields.some(
        (field) => !field || configuration.version === 3 && !supportedV3Field(field) || typeof field.name !== 'string' ||
          !isWorkflowFormLayoutSupported(configuration.version, field.layout) ||
          field.lookup != null && (configuration.version !== 3 || field.optionSource !== 'lookup' || !isWorkflowFormLookupBinding(field.lookup)) ||
          field.optionSource === 'lookup' && (configuration.version !== 3 || !isWorkflowFormLookupBinding(field.lookup) || field.options != null),
      ))
  );
}
/** The installed command owns field structure; these diagnostics describe permitted overrides. */
export function workflowFormFieldIssues(
  command: StepCommand,
  installed: StepAuthoringCommand,
): { form: string; fields: Record<string, string> } {
  if (isUnsupportedFormConfiguration(command))
    return {
      form: 'This saved form configuration is malformed or uses an unsupported version. Its settings are preserved.',
      fields: {},
    };
  const fields = installed.formFields ?? [];
  const missing =
    command.formConfiguration?.fields.filter(
      (field) => !fields.some((known) => known.name === field.name),
    ) ?? [];
  const layoutUnsupported = command.formConfiguration?.fields.some(setting => setting.layout != null &&
    fields.find(field => field.name === setting.name)?.supportsLayout !== true);
  const layouts = resolveWorkflowFormLayout(command.formConfiguration?.version ?? 1, fields.map(field => {
    const setting = command.formConfiguration?.fields.find(item => item.name === field.name);
    return { control: setting?.control ?? field.control, layout: setting?.layout };
  }));
  return {
    form: layoutUnsupported || !layouts
      ? 'This saved layout is unsupported by the installed action or has invalid row groups. Its settings are preserved.'
      : missing.length
      ? 'Some saved fields are unavailable. Restore the matching module before publishing.'
      : '',
    fields: Object.fromEntries(
      fields
        .map((field) => [field.name, fieldIssue(command, field, installed)])
        .filter(([, message]) => message),
    ),
  };
}

function fieldIssue(
  command: StepCommand,
  field: StepAuthoringFormField,
  installed: StepAuthoringCommand,
): string {
  const settings = command.formConfiguration?.fields.find(
    (item) => item.name === field.name,
  ) ?? { name: field.name };
  const changed = command.formConfiguration?.fields.some(
    (item) => item.name === field.name,
  );
  const source =
    settings.optionSource ??
    (changed
      ? 'contract-options'
      : field.optionSources.includes('step-outcomes')
        ? 'step-outcomes'
        : 'contract-options');
  const control = settings.control ?? field.control;
  const choices =
    source === 'custom-options'
      ? (settings.options ?? [])
      : source === 'step-outcomes'
        ? command.outcomes.map((outcome) => ({
            value: outcome.code,
            label: outcome.label,
          }))
        : field.options;
  if (
    settings.label != null &&
    (!settings.label.trim() || settings.label.length > 120)
  )
    return 'Enter a field label of 1–120 characters.';
  if (
    settings.maxLength != null &&
    (!Number.isSafeInteger(settings.maxLength) ||
      settings.maxLength < 1 ||
      settings.maxLength > (field.maxLength ?? 100000))
  )
    return `Enter a whole character limit from 1 to ${field.maxLength ?? 100000}.`;
  if (source === 'lookup') {
    if (command.formConfiguration?.version !== 3 || control !== 'select' || field.supportsLookup !== true || !field.optionSources.includes('lookup') || field.type !== 'string' || field.options.length)
      return 'This installed action does not permit lookup choices for this field. Its saved source is preserved.';
    return hasInstalledProvidedLookupRuntime(command, installed) || hasInstalledPreparationLookupRuntime(command, installed) || hasInstalledReviewRequestLookupRuntime(command, installed) ? ''
      : 'Lookup choices need a versioned action and runtime support. This saved source is preserved.';
  }
  if (control === 'select') {
    if (!field.optionSources.includes(source))
      return 'Select an available list source.';
    const options = choices;
    if (!options.length || options.length > 100)
      return 'Add between 1 and 100 choices.';
    if (
      options.some(
        (option) =>
          !option.value.trim() ||
          !option.label.trim() ||
          option.label.length > 120,
      )
    )
      return 'Each choice needs a stored value and a label of 1–120 characters.';
    if (hasInstalledReviewRequestLookupRuntime(command, installed) && options.some(option => option.value !== option.value.trim()))
      return 'Use stored choice values without leading or trailing whitespace so Request review can retain the selected code.';
    if (new Set(options.map((option) => option.value)).size !== options.length)
      return 'Each choice must have a unique stored value.';
    const maxLength =
      settings.maxLength ??
      field.maxLength ??
      (source === 'custom-options' ? 4000 : null);
    if (
      maxLength != null &&
      options.some((option) => option.value.length > maxLength)
    )
      return 'A stored choice exceeds the character limit.';
  }
  return '';
}
